import { request } from '../utils';

// TOTP two-factor management for the logged-in admin. All endpoints live under
// the admin API base (the proxy/baseURL adds the `/admin` prefix, so we call
// `/2fa` here just like the login-time interceptor in utils.js) and ride the
// authenticated session cookie.
//
// Every response uses the envelope { status, message, data, meta, request_id }:
// resource state comes from `data`, setup material (QR/secret) from `meta`. We
// hand the whole envelope back so callers can read whichever part they need.
const envelope = (res) => res.data;

// callApi (utils.js) drops the HTTP status code on rejection, but the 2FA flow
// has to tell 401 (retry) from 422 (restart) from 400 (validation). So we use
// the shared `request` instance directly and normalise the error to carry both
// the HTTP status and the server's user-facing message verbatim.
const fail = (err) => {
  if (err.response) {
    const body = err.response.data || {};
    const e = new Error(
      body.message || 'Something went wrong. Please try again.'
    );
    e.httpStatus = err.response.status;
    e.apiStatus = body.status;
    throw e;
  }
  // A navigation abort isn't a failure to report — hand it back untouched so
  // callers (and showError) can recognise `__cancelled`.
  if (err && err.__cancelled) throw err;
  const e = new Error(err.message || 'Network Error. Please try again.');
  e.httpStatus = 0;
  throw e;
};

// GET /admin/2fa -> data: { enabled, configured }. Drives the tab's render.
export const getTwoFactorStatus = () =>
  request.get('/2fa').then(envelope).catch(fail);

// POST /admin/2fa/setup -> meta: { otpauth_url, base32 }. Re-confirms identity
// by password and returns provisioning material for the authenticator app.
export const beginTwoFactorSetup = (password) =>
  request.post('/2fa/setup', { password }).then(envelope).catch(fail);

// POST /admin/2fa/setup/activation -> data: { configured, enabled }. Verifies a
// live code; only on success is 2FA actually turned on.
export const activateTwoFactorSetup = (token) =>
  request.post('/2fa/setup/activation', { token }).then(envelope).catch(fail);

// DELETE /admin/2fa -> data: { configured, enabled }. Requires both password
// and a current code. axios sends a DELETE body via the `data` config key.
export const disableTwoFactor = (password, token) =>
  request
    .delete('/2fa', { data: { password, token } })
    .then(envelope)
    .catch(fail);
